/**
 * SchemeCard.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * BeXyl IPM result card — 4-column layout matching reference JPEG:
 *
 *   PREVENTIVE | MONITORING | INTERVENTION | IPM SCHEME EVALUATION
 *
 * Evaluation column:
 *   - "Integrated performance" → mean score circle + confidence gauge
 *   - "Adoption readiness"     → mean score circle + confidence gauge
 *
 * Gauge: 3-zone semicircle (red → amber → green) with a black needle.
 * Score circle: coloured by value (green ≥7, amber 5–6.9, red <5).
 *
 * Real data comes from DB fields:
 *   int_perf_mean, int_perf_conf  (0-1 fraction from col M of Excel)
 *   adp_ready_mean, adp_ready_conf (future Excel cols N/O)
 *
 * SCORE BADGE: commented out in header — under discussion.
 */

import { useState } from "react";
const BASE     = "/api";
const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

// ── Authenticated blob fetch ───────────────────────────────────────────────────

async function fetchBlob(docId, endpoint) {
  const tok = getToken();
  const res = await fetch(`${BASE}/schemes/documents/${docId}/${endpoint}`, {
    headers: tok ? { "Authorization": `Bearer ${tok}` } : {},
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.blob();
}

async function downloadDoc(doc) {
  try {
    const blob = await fetchBlob(doc.id, "download");
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement("a"), { href: url, download: doc.filename });
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  } catch (e) { alert("Download failed: " + e.message); }
}

// ── Confidence gauge ──────────────────────────────────────────────────────────
// Semicircle (180° arc) divided into 3 equal zones:
//   Zone 1 (left)   = red    0 – 33%
//   Zone 2 (middle) = amber  33 – 66%
//   Zone 3 (right)  = green  66 – 100%
// value: 0-1 fraction (from DB) — 0.8 = 80% = needle points toward green

/* ── OLD GAUGE (sweep flags wrong — arc drew downward) — kept for reference ──
function ConfidenceGauge({ value, size = 65 }) {
  if (value == null || value === undefined) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <div style={{ width: size, height: Math.round(size * 0.55), display: "flex",
          alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 11, color: "#bbb" }}>—</span>
        </div>
      </div>
    );
  }
  const pct = value > 1 ? value / 100 : Number(value);
  const W = size, R = W * 0.43, ri = R * 0.52, cx = W / 2, cy = R + 5, H = cy + 8;
  function pt(deg, r) {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
  }
  function arcPath(a1, a2, ro, rin) {
    const [x1,y1]=pt(a1,ro),[x2,y2]=pt(a2,ro),[x3,y3]=pt(a2,rin),[x4,y4]=pt(a1,rin);
    const lg = Math.abs(a2-a1)>180?1:0;
    return `M${x1},${y1} A${ro},${ro} 0 ${lg},0 ${x2},${y2} L${x3},${y3} A${rin},${rin} 0 ${lg},1 ${x4},${y4} Z`;
  }
  const zones=[{a1:180,a2:120,fill:"#D63B2F"},{a1:120,a2:60,fill:"#E8A020"},{a1:60,a2:0,fill:"#4B7518"}];
  const needleDeg=180-pct*180,[nx,ny]=pt(needleDeg,R*.80),[b1x,b1y]=pt(needleDeg+90,3),[b2x,b2y]=pt(needleDeg-90,3);
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{display:"block",overflow:"visible"}}>
        {zones.map((z,i)=><path key={i} d={arcPath(z.a1,z.a2,R,ri)} fill={z.fill}/>)}
        {[60,120].map(a=>{const[lx1,ly1]=pt(a,ri),[lx2,ly2]=pt(a,R);return<line key={a} x1={lx1} y1={ly1} x2={lx2} y2={ly2} stroke="#fff" strokeWidth="2"/>;})}
        <polygon points={`${nx},${ny} ${b1x},${b1y} ${b2x},${b2y}`} fill="#111"/>
        <circle cx={cx} cy={cy} r="4" fill="#111"/><circle cx={cx} cy={cy} r="1.8" fill="#fff"/>
      </svg>
    </div>
  );
}


── END OLD GAUGE ── */

// ── FIXED GAUGE: upward-opening semicircle ────────────────────────────────────
// Key fix: outer arc sweep=1 (SVG clockwise = draws upward when y is negated),
//          inner arc sweep=0 (SVG counter-clockwise = draws back downward).
function ConfidenceGauge({ value, size = 65 }) {
  if (value == null || value === undefined) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <div style={{ width: size, height: Math.round(size * 0.58), display: "flex",
          alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 11, color: "#bbb" }}>—</span>
        </div>
      </div>
    );
  }

  const pct = value > 1 ? value / 100 : Number(value);

  const W  = size;
  const R  = W * 0.43;          // outer radius
  const ri = R * 0.50;          // inner radius → ring thickness = R*0.5
  const cx = W / 2;
  const cy = R + 4;             // pivot y: 4 px top-padding + arc radius
  const H  = cy + 9;            // SVG height: just enough for pivot dot

  // Standard math angles, mapped upward by negating y
  // 180° = left end, 90° = top centre, 0° = right end
  function pt(deg, r) {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
  }

  // Donut arc segment:
  //   outer arc: sweep=1 (SVG clockwise)  → travels upward through the top
  //   inner arc: sweep=0 (SVG counter-clockwise) → returns downward to close shape
  function arcPath(a1, a2, ro, rin) {
    const [x1, y1] = pt(a1, ro), [x2, y2] = pt(a2, ro);
    const [x3, y3] = pt(a2, rin), [x4, y4] = pt(a1, rin);
    const lg = Math.abs(a2 - a1) > 180 ? 1 : 0;
    return (
      `M${x1.toFixed(2)},${y1.toFixed(2)}` +
      ` A${ro},${ro} 0 ${lg},1 ${x2.toFixed(2)},${y2.toFixed(2)}` +
      ` L${x3.toFixed(2)},${y3.toFixed(2)}` +
      ` A${rin},${rin} 0 ${lg},0 ${x4.toFixed(2)},${y4.toFixed(2)} Z`
    );
  }

  const zones = [
    { a1: 180, a2: 120, fill: "#D63B2F" },   // red   — low confidence
    { a1: 120, a2:  60, fill: "#E8A020" },   // amber — medium
    { a1:  60, a2:   0, fill: "#4B7518" },   // green — high
  ];

  // Needle: pct=0 → points left (180°), pct=1 → points right (0°)
  const needleDeg  = 180 - pct * 180;
  const [nx,  ny]  = pt(needleDeg, R * 0.78);
  const [b1x, b1y] = pt(needleDeg + 90, 2.5);
  const [b2x, b2y] = pt(needleDeg - 90, 2.5);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      <svg
        width={W} height={H}
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: "block", overflow: "visible" }}
        aria-label={`Confidence: ${Math.round(pct * 100)}%`}
      >
        {zones.map((z, i) => (
          <path key={i} d={arcPath(z.a1, z.a2, R, ri)} fill={z.fill} />
        ))}

        {/* White dividers between zones */}
        {[60, 120].map(a => {
          const [lx1, ly1] = pt(a, ri);
          const [lx2, ly2] = pt(a, R);
          return <line key={a} x1={lx1.toFixed(2)} y1={ly1.toFixed(2)}
                       x2={lx2.toFixed(2)} y2={ly2.toFixed(2)}
                       stroke="#fff" strokeWidth="2" />;
        })}

        {/* Needle triangle */}
        <polygon
          points={`${nx.toFixed(2)},${ny.toFixed(2)} ${b1x.toFixed(2)},${b1y.toFixed(2)} ${b2x.toFixed(2)},${b2y.toFixed(2)}`}
          fill="#111"
        />
        <circle cx={cx} cy={cy} r="4"   fill="#111" />
        <circle cx={cx} cy={cy} r="1.8" fill="#fff" />
      </svg>
      <span style={{ fontSize: 9, fontWeight: 700, color: "#555",
        fontFamily: "'Nunito',sans-serif", lineHeight: 1 }}>
        {Math.round(pct * 100)}%
      </span>
    </div>
  );
}

// ── Mean score circle ─────────────────────────────────────────────────────────
// Coloured circle displaying the score with European comma decimal (7,4)

function MeanScoreCircle({ value, size = 44 }) {
  if (value == null || value === undefined) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%",
        background: "#f0f0f0", border: "2px solid #ccc",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 11, color: "#bbb" }}>—</span>
      </div>
    );
  }

  const v = typeof value === "number" ? value : parseFloat(value);
  let fg, bg, bd;
  if      (v >= 7) { fg = "#2d6b0a"; bg = "#ceeab0"; bd = "#4B7518"; }
  else if (v >= 5) { fg = "#7a4600"; bg = "#fde0a0"; bd = "#C87A00"; }
  else             { fg = "#8a1212"; bg = "#fbd8d8"; bd = "#C0392B"; }

  // European-style decimal with comma as in reference JPEG
  const label = v.toFixed(1).replace(".", ",");

  return (
    <div style={{ width: size, height: size, borderRadius: "50%",
      background: bg, border: `3px solid ${bd}`,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <span style={{ fontSize: size * 0.32, fontWeight: 800, color: fg,
        fontFamily: "'Nunito', sans-serif", lineHeight: 1 }}>
        {label}
      </span>
    </div>
  );
}

// ── Single evaluation row (Integrated performance OR Adoption readiness) ──────

function EvalRow({ label, mean, conf }) {
  return (
    <div style={{
      padding: "9px 0 7px",
      borderBottom: "1px solid rgba(212,217,128,.3)",
    }}>
      {/* Row label */}
      <div style={{ fontSize: 12, fontWeight: 700, color: "#4a5c2a",
        fontFamily: "'Nunito',sans-serif", marginBottom: 6 }}>
        {label}
      </div>
      {/* Mean + Gauge side by side */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
        {/* Mean score */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, flex: "0 0 auto" }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: "#888",
            textTransform: "uppercase", letterSpacing: ".05em" }}>
            Mean score
          </span>
          <MeanScoreCircle value={mean} size={44} />
        </div>
        {/* Confidence gauge */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, flex: 1 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: "#888",
            textTransform: "uppercase", letterSpacing: ".05em" }}>
            Confidence level
          </span>
          <ConfidenceGauge value={conf} />
        </div>
      </div>
    </div>
  );
}

// ── IPM scheme evaluation panel (4th column) ──────────────────────────────────

function EvaluationPanel({ scheme }) {
  const hasAny = scheme.int_perf_mean != null || scheme.adp_ready_mean != null;
  return (
    <div className="sc-eval">
      {/* Panel title */}
      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".07em", color: "#4B7518",
        borderBottom: "1px solid rgba(212,217,128,.5)", paddingBottom: 7, marginBottom: 2,
        display: "flex", alignItems: "center", gap: 5,
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2">
          <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
        </svg>
        IPM scheme evaluation
      </div>

      <EvalRow
        label="Integrated performance"
        mean={scheme.int_perf_mean}
        conf={scheme.int_perf_conf}
      />
      <EvalRow
        label="Adoption readiness"
        mean={scheme.adp_ready_mean}
        conf={scheme.adp_ready_conf}
      />
    </div>
  );
}

// ── Document buttons: eye (preview) + "↓ pdf" (download) ─────────────────────

function DocButtons({ doc }) {
  const [url,  setUrl]  = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function preview() {
    if (open) { setOpen(false); return; }
    if (url)  { setOpen(true);  return; }
    setBusy(true);
    try { const b = await fetchBlob(doc.id, "preview"); setUrl(URL.createObjectURL(b)); setOpen(true); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  const btn = {
    height: 26, border: "1px solid #c8d8b0", borderRadius: 5,
    background: "#fff", cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    color: "#4B7518", flexShrink: 0, padding: "0 7px",
  };

  return (
    <>
      <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
        {/* Eye — preview */}
        {doc.file_type === "pdf" && (
          <button onClick={preview} disabled={busy} title="Preview" style={{ ...btn, width: 26, padding: 0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        )}
        {/* Download */}
        <button onClick={() => downloadDoc(doc)} title="Download" style={{ ...btn, fontSize: 11, fontWeight: 700, gap: 3 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
          </svg>
          pdf
        </button>
      </span>

      {/* Preview modal */}
      {open && url && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setOpen(false)}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 16,
            width: "90vw", maxWidth: 980, height: "88vh", display: "flex", flexDirection: "column" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#5C4B00" }}>
                {doc.title || doc.filename}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => downloadDoc(doc)}
                  style={{ fontSize: 12, padding: "5px 14px", borderRadius: 7,
                    background: "#4B7518", color: "#fff", border: "none",
                    cursor: "pointer", fontWeight: 600 }}>
                  Download
                </button>
                <button onClick={() => setOpen(false)}
                  style={{ background: "none", border: "none", fontSize: 22,
                    cursor: "pointer", color: "#775F46", lineHeight: 1 }}>
                  ×
                </button>
              </div>
            </div>
            <iframe src={url} title={doc.filename}
              style={{ flex: 1, border: "1px solid #d4dbc8", borderRadius: 8 }} />
          </div>
        </div>
      )}
    </>
  );
}

// ── Option row ────────────────────────────────────────────────────────────────

function OptionRow({ option }) {
  return (
    <div style={{ padding: "5px 0", borderBottom: ".5px solid rgba(75,117,24,.08)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 14, color: "#2c3a1e", lineHeight: 1.4, flex: 1 }}>
          {option.option_text}
        </div>
        {option.documents?.length > 0 && (
          <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
            {option.documents.map(d => <DocButtons key={d.id} doc={d} />)}
          </div>
        )}
      </div>
      {option.constraint_text && (
        <div style={{ fontSize: 12, color: "#775F46", marginTop: 3, lineHeight: 1.45,
          borderLeft: "2px solid #D4D980", paddingLeft: 6 }}>
          {option.constraint_text.startsWith("•")
            ? option.constraint_text
            : `• ${option.constraint_text}`}
        </div>
      )}
    </div>
  );
}

// ── Measure column ────────────────────────────────────────────────────────────

const MCOLS = {
  prevention:   { bg: "#E4F0D4", tx: "#1e4a00", bd: "#b8d898", label: "Prevention",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
  monitoring:   { bg: "#F0E8CC", tx: "#3a2800", bd: "#d8c888", label: "Monitoring",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
  intervention: { bg: "#F0E0D8", tx: "#3a1400", bd: "#d8b8a8", label: "Intervention",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg> },
};

function MeasureColumn({ measure, options, noBorder }) {
  const m      = MCOLS[measure];
  const filled = (options || []).filter(o => o.option_text);
  return (
    <div className="sc-measure-col" style={{ flex: 1, minWidth: 0, padding: "12px 13px 10px",
      borderRight: noBorder ? "none" : "1px solid rgba(212,217,128,.4)" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5,
        fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em",
        padding: "3px 9px", borderRadius: 5, background: m.bg, color: m.tx,
        border: `1px solid ${m.bd}`, marginBottom: 9, fontFamily: "'Nunito',sans-serif" }}>
        {m.icon}{m.label}
      </div>
      {filled.length === 0
        ? <div style={{ fontSize: 12, color: "#9BC071", fontStyle: "italic" }}>—</div>
        : filled.map(o => <OptionRow key={o.id} option={o} />)
      }
    </div>
  );
}

// ── SchemeCard responsive styles (injected once) ─────────────────────────────

let _schemeStyleInjected = false;
function injectSchemeStyles() {
  if (_schemeStyleInjected || typeof document === "undefined") return;
  _schemeStyleInjected = true;
  const s = document.createElement("style");
  s.textContent = `
    .sc-body { display: flex; align-items: stretch; }
    .sc-eval  { width: 240px; flex-shrink: 0; border-left: 1.5px solid rgba(212,217,128,.5); background: #fafcf6; padding: 10px 12px 8px; }
    @media (max-width: 768px) {
      .sc-body { flex-direction: column !important; }
      .sc-eval  { width: 100% !important; border-left: none !important; border-top: 1.5px solid rgba(212,217,128,.5) !important; }
      .sc-measure-col { border-right: none !important; border-bottom: 1px solid rgba(212,217,128,.3) !important; }
    }
    @media print {
      .sc-body { display: flex !important; flex-direction: row !important; }
      .sc-eval  { width: 240px !important; border-left: 1.5px solid rgba(212,217,128,.5) !important; border-top: none !important; }
      .sc-measure-col { border-right: 1px solid rgba(212,217,128,.4) !important; border-bottom: none !important; }
    }
  `;
  document.head.appendChild(s);
}

// ── Main SchemeCard ────────────────────────────────────────────────────────────

export default function SchemeCard({ scheme, actors, objectives }) {
  injectSchemeStyles();
  const [expanded, setExpanded] = useState(true);

  const actorName = actors?.find(a => a.id === scheme.actor_id)?.name    ?? scheme.actor_id;
  const objName   = objectives?.find(o => o.id === scheme.objective_id)?.name ?? scheme.objective_id;
  const displayId = scheme.display_id ?? (
    scheme.scheme_id?.includes("_")
      ? scheme.scheme_id.split("_").slice(1).join("_")
      : scheme.scheme_id
  );

  const measures   = scheme.measures  ?? {};
  const schemeDocs = scheme.documents ?? [];

  return (
    <div className="print-card" style={{ background: "#fff", border: "1.5px solid #D4D980",
      borderRadius: 13, marginBottom: 14, overflow: "hidden",
      boxShadow: "0 2px 8px rgba(75,117,24,.06)" }}>

      {/* ── Header bar ── */}
      <div style={{ background: "#4B7518", padding: "10px 14px",
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>

        {/* Path: Actor → Objective → Complexity → Scheme N | ID badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, flex: 1,
          fontSize: 13, fontWeight: 700, color: "#fff",
          flexWrap: "wrap", fontFamily: "'Nunito',sans-serif" }}>
          <span>{actorName}</span>
          {/* <span style={{ color: "#8a8f1a" }}>→</span> */}
          <span style={{ color: "#ccc" }}>→</span>
          <span>{objName}</span>
          <span style={{ color: "#ccc" }}>→</span>
          <span>{scheme.complexity}</span>
          <span style={{ color: "#ccc" }}>→</span>
          <span>Scheme {scheme.scheme_number}</span>
          <span style={{ background: "#ccc", color: "#4B7518", fontSize: 12,
            fontWeight: 700, padding: "2px 10px", borderRadius: 5,
            border: "1.5px solid #4B7518", marginLeft: 4 }}>
            {displayId}
          </span>
        </div>

        {/* Right: [score badge — COMMENTED OUT] + general leaflet + collapse */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>

          {/*
          ── SCORE BADGE — commented out, pending evaluation discussion ──────
          {scheme.score != null && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20,
              background: "#4B7518", color: "#fff", fontFamily: "'Nunito',sans-serif" }}>
              ★ {scheme.score_label || `${scheme.score.toFixed(1)} / 10`}
            </span>
          )}
          ── END SCORE BADGE ─────────────────────────────────────────────────
          */}

          {/* General leaflet — separator + label + icon buttons, no filename */}
          {schemeDocs.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 7,
              borderLeft: "1.5px solid rgba(92,75,0,.22)", paddingLeft: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#D4D980",
                textTransform: "uppercase", letterSpacing: ".05em",
                fontFamily: "'Nunito',sans-serif" }}>
                General leaflet
              </span>
              {schemeDocs.map(d => <DocButtons key={d.id} doc={d} />)}
            </div>
          )}

          {/* Collapse/expand toggle */}
          <button onClick={() => setExpanded(v => !v)} style={{ width: 28, height: 28,
            background: "rgba(92,75,0,.12)", border: "none", borderRadius: 6,
            cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", color: "#5C4B00" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.5">
              {expanded ? <path d="M5 15l7-7 7 7"/> : <path d="M19 9l-7 7-7-7"/>}
            </svg>
          </button>
        </div>
      </div>

      {/* Expert comment (below header if present) */}
      {/* {expanded && scheme.expert_comment && (
        <div style={{ padding: "7px 14px", background: "#FAEEDA",
          borderBottom: "1px solid #FAC775", fontSize: 12, color: "#633806" }}>
          <strong>Expert comment:</strong> {scheme.expert_comment}
        </div>
      )} */}

      {/* ── Body: 3 measure columns + evaluation panel ── */}
      {expanded && (
        <div className="sc-body">
          <MeasureColumn measure="prevention"   options={measures.prevention   ?? []} />
          <MeasureColumn measure="monitoring"   options={measures.monitoring   ?? []} />
          <MeasureColumn measure="intervention" options={measures.intervention ?? []} noBorder />
          <EvaluationPanel scheme={scheme} />
        </div>
      )}
    </div>
  );
}
